import _ from 'lodash';

import { handleErrors } from '../../../actions/ErrorActions';
import { getSupportData } from '../../../api/get-support-data';
import { orderOptions } from '../../../utils/formdataOrderUtils';
import { project } from '../../../utils/ramdaUtils';
import { STATIC_DATA_SOURCE_LIST } from '../../constants/form-manager.constants';
import { evaluateConditions } from '../evaluation.utils';
import {
  applyFilterToDataSource,
  getFullDataSetByDataMember,
  getSupportDataRequestFilter,
} from '../form-manager.utils';

export const mapOrderByArrayToSortExpression = (orderByArr) => {
  return orderByArr
    ? orderByArr.reduce((acc, item, index) => {
        const divider = index < orderByArr.length - 1 ? ', ' : '';
        return acc + `${item.field} ${item.direction}${divider}`;
      }, '')
    : undefined;
};

export const supportDataAdapter = (rows, columnsToAdd) => {
  const supportDataTable = [];

  try {
    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        const supportDataRow = { id: row.id };

        if (Array.isArray(columnsToAdd)) {
          columnsToAdd.forEach((columnName, j) => {
            let columnValue = row.cells[j];

            if (typeof columnValue === 'string') {
              columnValue = columnValue.trim();
            }

            supportDataRow[columnName] = columnValue;
          });
        }
        supportDataTable.push(supportDataRow);
      });
    }
  } catch (Error) {
    // eslint-disable-next-line no-console
    console.log(`supportDataAdapter() ${Error}`);
  }

  return supportDataTable;
};

export async function fetchSupportDataSourceFromServer({
  dataSource,
  dataSourceFilter = {},
  dataSourceFilterFromDataSource,
  ssContext,
  pageLimit,
  isEditor,
  dispatch,
  isGridInitialization,
  disableCache,
}) {
  const apiFilter = [];

  if (dataSourceFilterFromDataSource?.Conditions?.length) {
    apiFilter.push(
      ...getSupportDataRequestFilter(dataSourceFilterFromDataSource.Conditions, ssContext)
    );
  }

  const orderByArr = dataSource.orderBy;

  if (orderByArr && orderByArr.length > 0) {
    dataSource.sortExpression = mapOrderByArrayToSortExpression(orderByArr);
  }

  dataSource.getTotalRows = true;
  let loopPages = false;
  // run paginated data fetch only when using default pageNumber and pageSize.
  // dataSourceTypes 4 and 6 always bring back all the data, so no need to do this for them
  if (
    dataSource.pageNumber === 1 &&
    dataSource.pageSize === 0 &&
    dataSource.dataSourceType !== 4 &&
    dataSource.dataSourceType !== 6
  ) {
    loopPages = true;
  }
  let pageCount = 1;

  const providedValues = !isEditor
    ? dataSource.runtimeColumnList
    : dataSource.dataSourceTypeStr === 'LISTDATA'
    ? STATIC_DATA_SOURCE_LIST
    : dataSource.columnList;

  const preparedDataSource = {
    ...dataSource,
    columnList: providedValues,
    filter: apiFilter,
  };

  delete preparedDataSource.containerFormName;
  delete preparedDataSource.dataSourceKey;
  delete preparedDataSource.runtimeColumnList;

  try {
    let dataRows = [];

    do {
      const data = await getSupportData(preparedDataSource, disableCache);
      if (data.hasError && isEditor) {
        dispatch(handleErrors(data.errors));
      }
      if (_.isNil(data.GetSupportData)) {
        break;
      }
      pageCount = data.GetSupportData.total;
      dataRows = [...dataRows, ...data.GetSupportData.rows];
      preparedDataSource.pageNumber = preparedDataSource.pageNumber + 1;
    } while (loopPages && preparedDataSource.pageNumber <= (pageLimit ? pageLimit : pageCount));

    if (dataRows.length !== 0) {
      const parsedSupportData = supportDataAdapter(
        dataRows,
        [...providedValues, 'LNG_TRANSLATION'],
        false
      );

      const filteredValuesForListData = project(
        [...dataSource.columnList, 'LNG_TRANSLATION'],
        parsedSupportData
      );

      const parsedDataSourceValue =
        isEditor && dataSource.dataSourceTypeStr === 'LISTDATA'
          ? filteredValuesForListData
          : parsedSupportData;

      const filter = [
        ...(dataSourceFilter?.Conditions ?? []),
        ...(dataSourceFilterFromDataSource?.Conditions ?? []),
      ];

      return !isGridInitialization
        ? applyFilterToDataSource(ssContext, parsedDataSourceValue, filter)
        : parsedDataSourceValue;
    }
    return [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Fetching get support data failed: ${JSON.stringify(error.stack)}`);
    return [];
  }
}

export async function fetchDataSourceFromLocalStore({
  dataSource,
  dataSourceFilter = {},
  ssContext,
  isEditor,
}) {
  // get all rows disregard of index
  let rows = getFullDataSetByDataMember(ssContext.data, dataSource.dataSourceName);
  const selectedFields = isEditor ? dataSource.columnList : dataSource.runtimeColumnList;

  // apply filters
  if (dataSourceFilter?.Conditions && Array.isArray(rows)) {
    rows = rows.filter((row) =>
      evaluateConditions({
        conditions: dataSourceFilter.Conditions,
        ssContext,
        datasourceRow: row,
      })
    );
  }

  // sort rows
  if (dataSource.orderBy?.length && Array.isArray(rows)) {
    rows = orderOptions(rows, dataSource.orderBy);
  }

  // get only requested columns
  rows = Array.isArray(rows)
    ? rows.map((row) =>
        selectedFields.reduce((object, key) => {
          object[key] = row[key];
          return object;
        }, {})
      )
    : [];

  return rows;
}

export async function getLanguageDataSource({ dataSource, dataSourceFilter, ssContext, isEditor }) {
  let rows = [];
  const { languages } = ssContext.translations;
  const selectedFields = isEditor ? dataSource.columnList : dataSource.runtimeColumnList;

  if (languages) {
    rows = Object.entries(languages).map(([key, value]) => {
      const languageObject = { LANGUAGE_CODE: value, LANGUAGE_NAME: key };
      return selectedFields?.reduce((object, key) => {
        object[key] = languageObject[key];
        return object;
      }, {});
    });
  }

  // apply dataSource filters
  if (dataSource.filter?.Conditions && Array.isArray(rows)) {
    rows = rows.filter((row) =>
      evaluateConditions({
        conditions: dataSource.filter.Conditions,
        ssContext,
        datasourceRow: row,
      })
    );
  }

  // apply control filters
  if (dataSourceFilter?.Conditions && Array.isArray(rows)) {
    rows = rows.filter((row) =>
      evaluateConditions({
        conditions: dataSourceFilter.Conditions,
        ssContext,
        datasourceRow: row,
      })
    );
  }

  // sort rows
  if (dataSource.orderBy?.length && Array.isArray(rows)) {
    rows = orderOptions(rows, dataSource.orderBy);
  }

  return rows;
}
